import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Prediction, Channel, Server, QuotaInfo, PredictionStatus, ChannelVisit } from '../types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      setUser: (user) => set({ user, isAuthenticated: !!user, isLoading: false }),
      setLoading: (isLoading) => set({ isLoading }),
      logout: () => set({ user: null, isAuthenticated: false }),
    }),
    {
      name: 'foretell-auth',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);

interface ServerState {
  currentServer: Server | null;
  servers: Server[];
  channels: Channel[];
  currentChannel: Channel | null;
  channelVisits: ChannelVisit[];
  setCurrentServer: (server: Server | null) => void;
  setServers: (servers: Server[]) => void;
  setChannels: (channels: Channel[]) => void;
  setCurrentChannel: (channel: Channel | null) => void;
  setChannelVisits: (visits: ChannelVisit[]) => void;
}

export const useServerStore = create<ServerState>()((set) => ({
  currentServer: null,
  servers: [],
  channels: [],
  currentChannel: null,
  channelVisits: [],
  setCurrentServer: (server) => set({ currentServer: server }),
  setServers: (servers) => set({ servers }),
  setChannels: (channels) => set({ channels }),
  setCurrentChannel: (channel) => set({ currentChannel: channel }),
  setChannelVisits: (channelVisits) => set({ channelVisits }),
}));

interface PredictionState {
  predictions: Prediction[];
  selectedPrediction: Prediction | null;
  filter: {
    status: PredictionStatus | 'all';
    search: string;
    sortBy: 'newest' | 'deadline' | 'confidence' | 'votes';
  };
  setPredictions: (predictions: Prediction[]) => void;
  addPrediction: (prediction: Prediction) => void;
  updatePrediction: (id: string, updates: Partial<Prediction>) => void;
  removePrediction: (id: string) => void;
  setSelectedPrediction: (prediction: Prediction | null) => void;
  setFilter: (filter: Partial<PredictionState['filter']>) => void;
}

export const usePredictionStore = create<PredictionState>()((set) => ({
  predictions: [],
  selectedPrediction: null,
  filter: {
    status: 'all',
    search: '',
    sortBy: 'newest',
  },
  setPredictions: (predictions) => set({ predictions }),
  addPrediction: (prediction) =>
    set((state) => ({ predictions: [prediction, ...state.predictions] })),
  updatePrediction: (id, updates) =>
    set((state) => ({
      predictions: state.predictions.map((p) =>
        p.id === id ? { ...p, ...updates } : p
      ),
    })),
  removePrediction: (id) =>
    set((state) => ({
      predictions: state.predictions.filter((p) => p.id !== id),
    })),
  setSelectedPrediction: (prediction) => set({ selectedPrediction: prediction }),
  setFilter: (filter) =>
    set((state) => ({ filter: { ...state.filter, ...filter } })),
}));

interface QuotaState {
  quota: QuotaInfo | null;
  hasAccess: boolean;
  setQuota: (quota: QuotaInfo) => void;
  setHasAccess: (hasAccess: boolean) => void;
  canPost: () => boolean;
}

export const useQuotaStore = create<QuotaState>()((set, get) => ({
  quota: null,
  hasAccess: false,
  setQuota: (quota) => set({ quota }),
  setHasAccess: (hasAccess) => set({ hasAccess }),
  canPost: () => {
    const { quota, hasAccess } = get();
    if (hasAccess) return true;
    if (!quota) return false;
    return quota.hasUnlimited || quota.used < quota.limit;
  },
}));

interface UIState {
  view: 'channels' | 'feed' | 'create' | 'detail' | 'history' | 'analytics' | 'settings';
  sidebarOpen: boolean;
  modalOpen: string | null;
  toast: { message: string; type: 'success' | 'error' | 'info' } | null;
  setView: (view: UIState['view']) => void;
  setSidebarOpen: (open: boolean) => void;
  openModal: (modal: string) => void;
  closeModal: () => void;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
  hideToast: () => void;
}

export const useUIStore = create<UIState>()((set) => ({
  view: 'feed',
  sidebarOpen: false,
  modalOpen: null,
  toast: null,
  setView: (view) => set({ view }),
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  openModal: (modal) => set({ modalOpen: modal }),
  closeModal: () => set({ modalOpen: null }),
  showToast: (message, type) => set({ toast: { message, type } }),
  hideToast: () => set({ toast: null }),
}));
